import { dialog } from "nq-component";
import BaseFormPresenter from "../../../../base/BaseFormPresenter";
import DialogModal from "../../../../components/Dialog";
import { findObjectUseCase } from "../../../../usecases/object";
import { auditLogUseCase } from "../../../../usecases/logs";

class PullOutFormPresenter extends BaseFormPresenter {
  async onChange(value, field) {
    this.change[field] = value;
    this.object[field] = value;

    const view = this.view;

    // Click X
    if (field === "component" && (!value || Object.keys(value).length === 0)) {
      view.setState({
        object: {
          ...view.state.object,
          component: null,
          componentId: null,
          quantity: "",
          raw_materials: [],
        },
        componentBaseQty: null,
        rawMaterialBase: [],
      });
      return;
    }

    // Selecting a component
    if (field === "component" && value?.id) {
      try {
        const [component] = await findObjectUseCase().execute("components", {
          where: { id: value.id },
          limit: 1,
        });

        if (component && Array.isArray(component.raw_materials)) {
          const baseQty = parseFloat(component.quantity) || 1;
          const baseRaw = component.raw_materials.map((item) => ({
            ...item,
            quantity: parseFloat(item.quantity) || 0,
          }));

          view.setState((prev) => ({
            object: {
              ...prev.object,
              component: value,
              componentId: component.id,
              quantity: 0,
              raw_materials: baseRaw.map((item) => ({
                ...item,
                quantity: 0,
              })),
            },
            componentBaseQty: baseQty,
            rawMaterialBase: baseRaw,
          }));
        }
      } catch (err) {
        console.error("Error loading component", err);
      }
      return;
    }

    // Quantity field changed
    if (field === "quantity") {
      const newQty = parseFloat(value) || 0;
      const baseQty = view.state.componentBaseQty || 1;
      const rawBase = view.state.rawMaterialBase;

      const multiplier = newQty / baseQty;

      const scaledRaw = rawBase?.map((item) => ({
        ...item,
        quantity: Math.round((item.quantity || 0) * multiplier),
      }));

      view.setState({
        object: {
          ...view.state.object,
          quantity: newQty,
          raw_materials: scaledRaw,
        },
      });
      return;
    }

    // Fallback generic field change
    view.setObject({
      ...view.state.object,
      [field]: value,
    });
  }

  async getObject() {
    const collection = this.view.getCollectionName();
    const id = this.object.id || this.view.getObjectId();
    if (id) {
      const params = {
        include: [
          "component",
          "component.uom",
          "raw_materials",
          "raw_materials.uom",
          "uom",
        ],
      };
      try {
        this.view.showProgress();
        this.object = await this.getObjectUseCase.execute(collection, id, {
          params,
        });
        this.view.hideProgress();
        this.view.setObject(this.object);
      } catch (error) {
        this.view.hideProgress();
        this.view.showError(error);
      }
    }
  }

  async save() {
    this.view.showProgressDialog();
    const collection = this.view.getCollectionName();
    const user = this.view.getCurrentUser();

    if (this.object.id) {
      this.change.id = this.object.id;
    } else {
      this.change.acl = this.view.getAcl();
    }

    this.change.component = { id: this.change.component.id };
    this.change.rawMaterials = this.view.state.object.raw_materials;
    this.change.storeId = { id: user.storeAnalytic[0]?.id };

    try {
      // 1. Save pullout to backup_pullouts collection
      const response = await this.upsertUseCase.execute(
        collection,
        this.change
      );

      // 2. Deduct raw_material quantities from inventory
      const rawMaterials = this.view.state.object.raw_materials || [];

      for (const raw of rawMaterials) {
        const rawId = raw.id;
        const rawItemName = raw.itemName;
        const pulloutQty = parseFloat(raw.quantity) || 0;

        if (!rawId || isNaN(pulloutQty)) continue;

        // 3. Fetch current inventory record
        const [inv] = await findObjectUseCase().execute("inventory", {
          where: {
            itemName: { $eq: rawItemName },
            "storeId.id": { $eq: user?.storeAnalytic?.[0]?.id },
          },
          limit: 1,
        });

        if (inv && inv.systemCount !== undefined) {
          const currentStock = parseFloat(inv.systemCount) || 0;
          const newStock = currentStock - pulloutQty;

          // 4. Save updated inventory record
          const responseInv = await this.upsertUseCase.execute("inventory", {
            id: inv.id,
            systemCount: (newStock < 0 ? 0 : newStock).toString(),
          });

          await auditLogUseCase().create({
            user: { id: user.id },
            logDate: new Date().toISOString(),
            message: `Deducting ${pulloutQty} from ${inv.itemName}, new stock: ${newStock}`,
            collection,
            objectId: response.id,
            payload: {
              savingPofb: response || null,
              updatingInventory: responseInv,
            },
            store: { id: user.storeAnalytic[0]?.id },
          });
        }
      }

      // 5. Reset change object
      this.change = {};
      this.view.hideProgressDialog();
    } catch (error) {
      console.error("Error during save/deduct:", error);
      throw error;
    }
  }

  async submit() {
    // if (Object.values(this.change).length === 0) {
    //   this.view.showSuccessSnackbar("Successfully saved!");
    //   return;
    // }
    try {
      this.view.submitting();
      dialog.close();
      await this.save();

      dialog.fire({
        html: (
          <DialogModal
            icon="bi bi-question-octagon-fill"
            iconColor="#82c14a"
            title="Saved Successfully"
            message=""
            isCancelButton={false}
            onSubmit={() => {
              this.view.navigateBack();
              dialog.close();
            }}
            onClose={() => dialog.close()}
          />
        ),
        footer: false,
      });
    } catch (error) {
      this.view.submissionError(error);
      this.view.showError(error);
    }
  }
}

export default PullOutFormPresenter;
